import { Box, Button, Grid, TextField } from '@mui/material'
import { ErrorDialog, HideDialog, SuccessDialog } from '../Dialogs'
import React, { useEffect, useState } from 'react'
import Autocomplete from '@mui/material/Autocomplete'
import HideEedService from '../service/hideEed'
import styled from 'styled-components'

const HiddenFieldLabel = styled.label`
	text-transform: capitalize;
`

function HideEEDFields({ clientsService }) {
  const [clients, setClients] = useState([])
  const [allClients, setAllClients] = useState([])
  const [allClientsError, setAllClientsError] = useState(false)
  const [allClientsPlaceholder, setAllClientsPlaceholder] =
		useState('Loading...')
  const [hiddenFields, setHiddenFields] = useState({
    'benefit status': false,
    'posting title': false,
    status: false,
  })
  const [hiddenFieldsDisabled, setHiddenFieldsDisabled] = useState(false)
  const [showDialog, setShowDialog] = useState(false)
  const [showErrorDialog, setShowErrorDialog] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [showSuccessDialog, setShowSuccessDialog] = useState(false)

  function ListHiddenFields({ fields }) {
    return Object.keys(fields).map(key => (
      <Grid item key={key} xs={12}>
        <HiddenFieldLabel>
          <input
            checked={hiddenFields[key]}
            disabled={hiddenFieldsDisabled}
            id={key}
            name={key}
            onChange={handleCheckboxChange}
            type="checkbox"
          />
          {key}
        </HiddenFieldLabel>
      </Grid>
    ))
  }

  const handleCheckboxChange = event => {
    const name = event.target.name
    const value = event.target.checked
    setHiddenFields(values => ({ ...values, [name]: value }))
  }

  const handleSelectChange = (event, values) => {
    setClients(values)
    if (values.length === 1) {
      clearHiddenFields()
      setHiddenFieldsDisabled(true)
      const client = values[0].value
      HideEedService.getHidden(client)
        .then(function (results) {
          try {
            if (typeof results.results[0][client] != 'undefined') {
              const settings = results.results[0][client]
              const newFields = { ...hiddenFields }
              settings.forEach(field => (newFields[field] = true))
              setHiddenFields(newFields)
            }
          } catch (e) {
            showError('Failed to retrieve properties for client.')
          } finally {
            setHiddenFieldsDisabled(false)
          }
        })
        .catch()
    } else if (values.length === 0) {
      clearHiddenFields()
    }
  }

  const handleSubmit = event => {
    event.preventDefault()
    setShowDialog(true)
  }

  const clear = () => {
    setClients([])
    clearHiddenFields()
  }

  const clearHiddenFields = () => {
    const clearedFields = Object.keys(hiddenFields).reduce((acc, key) => {
      return { ...acc, [key]: false }
    }, {})
    setHiddenFields(clearedFields)
  }

  const hide = async () => {
    try {
      const result = await HideEedService.postHidden(clients, hiddenFields)
      if (result.status === 200) {
        setShowSuccessDialog(true)
        setTimeout(() => setShowSuccessDialog(false), 2000)
        setShowDialog(false)
      }
    } catch (e) {
      showError(`The fields were not hidden successfully: ${e}`)
    }
  }

  const showError = message => {
    setErrorMessage(message)
    setShowErrorDialog(true)
  }

  useEffect(() => {
    clientsService
      .getAllClients()
      .then(function (result) {
        setAllClients(result)
        setAllClientsError(false)
        setAllClientsPlaceholder('Select Clients...')
      })
      .catch(function () {
        setAllClientsError(true)
        setAllClientsPlaceholder('Error loading clients...')
      })
  }, [])

  return (
    <div>
      <Box padding={3.5}>
        <h2>Hide Fields in Edit Employee Details (EED)</h2>
        <p>
					Currently, only three fields can be hidden in EED: Posting Type,
					Benefit Status, and Status. This tool allows a quick way to make these
					configurations.
        </p>
        <p>
					You can hide the same fields for any number of clients by looking them
					up in the Clients field and checking the boxes for the field(s) you
					would like to hide. This is a <b> destructive process </b>. Meaning
					anything left unchecked will not be hidden.
        </p>
        <p>
					To look up a client&apos;s settings, simply enter the client in as the
					only client in the Clients field.
        </p>
        <form onSubmit={handleSubmit}>
          <Grid
            container
            direction="column"
            justifyContent="space-around"
            spacing={4}
          >
            <Grid container item spacing={1}>
              <Grid item xs={12}>
                <Autocomplete
                  data-testid="autocomplete"
                  defaultValue={[]}
                  disabled={allClientsError}
                  getOptionLabel={option => option.label}
                  id="tags-standard"
                  multiple
                  onChange={handleSelectChange}
                  options={allClients}
                  renderInput={params => (
                    <TextField
                      {...params}
                      data-testid="text-field-test"
                      error={allClientsError}
                      label={allClientsPlaceholder}
                      placeholder="Clients"
                      variant="standard"
                    />
                  )}
                  value={clients}
                />
              </Grid>
              <ListHiddenFields fields={hiddenFields} />
              <Grid
                alignItems="center"
                container
                item
                justifyContent="space-around"
              >
                <Grid item>
                  <Button
                    color="primary"
                    disabled={clients.length === 0}
                    type="submit"
                    variant="contained"
                  >
										Hide
                  </Button>
                </Grid>
              </Grid>
              <Grid
                alignItems="center"
                container
                item
                justifyContent="space-around"
              >
                <Grid item>
                  <Button color="primary" onClick={clear}>
										Clear
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </form>
      </Box>
      <HideDialog
        hide={hide}
        onClose={() => setShowDialog(false)}
        open={showDialog}
      />
      <ErrorDialog
        message={errorMessage}
        onClose={() => setShowErrorDialog(false)}
        open={showErrorDialog}
      />
      <SuccessDialog
        message="The selected fields were hidden for the selected clients"
        onClose={() => setShowSuccessDialog(false)}
        open={showSuccessDialog}
      />
    </div>
  )
}

export default HideEEDFields
